import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError, of } from 'rxjs';
import { catchError, map, tap, filter, take } from 'rxjs/operators';
import { Token } from './auth.types';

@Injectable({
	providedIn: 'root',
})
export class TokenManagerService {
	/**
	 * Subject for notifying subscribers when a refresh completes.
	 * Emits the new access token on success, or a special failure signal.
	 */
	private refreshTokenSubject = new BehaviorSubject<string | null>(null);

	/**
	 * Subject to signal refresh failure to queued requests.
	 * This allows queued requests to fail gracefully instead of hanging.
	 */
	private refreshFailedSubject = new Subject<Error>();

	/**
	 * Internal flag to track if a refresh is in progress.
	 * This is the single source of truth for refresh state.
	 */
	private _isRefreshing = false;

	/**
	 * AuthService reference, set by AuthService during initialization
	 * to break the circular dependency.
	 */
	private _authService: any;

	constructor() {}

	/**
	 * Set the AuthService reference. Called by AuthService during initialization
	 * to break the circular dependency.
	 */
	public setAuthService(authService: any): void {
		this._authService = authService;
	}

	/**
	 * Get the AuthService reference.
	 */
	private get authService(): any {
		return this._authService;
	}

	/**
	 * Check if a token refresh is currently in progress.
	 * This is the single source of truth for refresh state.
	 */
	public get isRefreshing(): boolean {
		return this._isRefreshing;
	}

	/**
	 * Observable that emits the new token when refresh completes.
	 * Use this to queue requests that need to wait for a refresh to complete.
	 */
	public get refreshToken$(): Observable<string | null> {
		return this.refreshTokenSubject.asObservable();
	}

	/**
	 * Observable that emits when refresh fails.
	 * Queued requests can subscribe to this to fail gracefully.
	 */
	public get refreshFailed$(): Observable<Error> {
		return this.refreshFailedSubject.asObservable();
	}

	/**
	 * Signal that a refresh has completed with a new token or failed (null).
	 */
	public notifyRefreshComplete(token: string | null): void {
		this.refreshTokenSubject.next(token);
	}

	/**
	 * Get the current access token as an observable.
	 * Returns the token synchronously without side effects.
	 */
	getAccessToken(): Observable<string | null> {
		const token = this.authService.token?.access || null;
		return of(token);
	}

	/**
	 * Refresh the access token using the refresh token.
	 * If a refresh is already in progress, queues the request and waits for the result.
	 *
	 * @param storedRefresh Optional refresh token to use (for startup refresh from storage)
	 * @param tokenType Optional token type ('Bearer' for OAuth, undefined for JWT)
	 */
	refreshToken(storedRefresh?: string, tokenType?: string): Observable<string> {
		if (this._isRefreshing) {
			// Queue this request - wait for the ongoing refresh to complete
			// Use race between success and failure to handle both outcomes
			return new Observable<string>((subscriber) => {
				const tokenSub = this.refreshTokenSubject.pipe(
					filter((token: string | null) => token !== null),
					take(1)
				).subscribe({
					next: (token) => {
						subscriber.next(token!);
						subscriber.complete();
					}
				});

				const failSub = this.refreshFailedSubject.pipe(
					take(1)
				).subscribe({
					next: (error) => {
						subscriber.error(error);
					}
				});

				// Cleanup subscriptions when the observable is unsubscribed
				return () => {
					tokenSub.unsubscribe();
					failSub.unsubscribe();
				};
			});
		}

		// Use provided refresh token or get from current token
		const refreshTokenValue = storedRefresh || this.authService.token?.refresh;
		if (!refreshTokenValue) {
			return throwError(() => new Error('No refresh token available.'));
		}

		// Set refreshing flag - this is now the single source of truth
		this._isRefreshing = true;

		// Signal that refresh is starting (for request queuing)
		this.refreshTokenSubject.next(null);

		return this.authService.renewToken(storedRefresh, tokenType).pipe(
			tap((newToken: Token) => {
				// Emit the new token to all waiting subscribers
				this._isRefreshing = false;
				this.refreshTokenSubject.next(newToken.access);
			}),
			map((newToken: Token) => newToken.access),
			catchError((error) => {
				// Signal failure to all waiting subscribers
				this._isRefreshing = false;
				this.refreshFailedSubject.next(error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * Get the refresh token.
	 */
	private getRefreshToken(): string | null {
		return this.authService.token?.refresh || null;
	}

	/**
	 * Log out the user and clear any token-related state.
	 */
	logout(): void {
		this.authService.logout();
		this.refreshTokenSubject.next(null);
	}
}
